import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from './shared/AuthContext.tsx';
import { loginGoogle } from './shared/api.ts';
import type { Rol } from './shared/tipos.ts';

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? '';
const GSI_SRC = 'https://accounts.google.com/gsi/client';

const RUTA_POR_ROL: Record<Rol, string> = {
  admin: '/admin',
  operador: '/admin',
  cliente: '/cliente',
  personal: '/personal',
};

export function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const botonGoogle = useRef<HTMLDivElement>(null);

  // Botón "Ingresar con Google" (solo si hay client ID configurado). El script
  // de Google se carga una vez; el callback recibe el ID token y lo manda al Worker.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !botonGoogle.current) return;
    let cancelado = false;
    const render = () => {
      if (cancelado || !botonGoogle.current || typeof google === 'undefined') return;
      google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async ({ credential }) => {
          setError(null);
          setEnviando(true);
          try {
            const u = await loginGoogle(credential);
            window.location.href = RUTA_POR_ROL[u.rol];
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al ingresar con Google');
            setEnviando(false);
          }
        },
      });
      google.accounts.id.renderButton(botonGoogle.current, {
        theme: 'outline', size: 'large', shape: 'pill', text: 'continue_with', locale: 'es', width: 320,
      });
    };
    const existente = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (existente && typeof google !== 'undefined') {
      render();
    } else {
      const script = existente ?? Object.assign(document.createElement('script'), { src: GSI_SRC, async: true, defer: true });
      script.addEventListener('load', render);
      if (!existente) document.head.appendChild(script);
    }
    return () => {
      cancelado = true;
    };
  }, []);

  async function alEnviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      window.location.href = RUTA_POR_ROL[u.rol];
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al iniciar sesión');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-superficie px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 h-14 w-14 rounded-2xl bg-primario-400 p-3 text-white shadow-lg">
            <LogoSvg />
          </div>
          <h1 className="text-2xl font-bold text-neutro-800">CONVOCA</h1>
          <p className="text-sm text-neutro-500">Coordinación de personal gastronómico</p>
        </div>

        <form
          onSubmit={alEnviar}
          className="space-y-4 rounded-2xl bg-white p-6 shadow-xl border border-contorno-var"
        >
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Email</span>
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="campo"
              placeholder="tu@email.com"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Contraseña</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="campo"
              placeholder="••••••••"
            />
          </label>

          {error && (
            <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-estado-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={enviando}
            className="boton w-full bg-primario-400 text-white hover:bg-primario-500"
          >
            {enviando ? 'Entrando…' : 'Entrar'}
          </button>

          {GOOGLE_CLIENT_ID && (
            <div className="border-t border-contorno-var pt-4">
              <p className="mb-2 text-center text-xs text-neutro-400">o con tu cuenta de Google (tiene que ser el mismo email que te dio el administrador)</p>
              <div ref={botonGoogle} className="flex justify-center" />
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

function LogoSvg() {
  return (
    <svg viewBox="0 0 64 64" className="h-full w-full">
      <path
        d="M20 14h24a2 2 0 0 1 2 2v6a6 6 0 0 1-6 6 6 6 0 0 1-4-1.5A6 6 0 0 1 32 28a6 6 0 0 1-4-1.5A6 6 0 0 1 24 28a6 6 0 0 1-6-6v-6a2 2 0 0 1 2-2z"
        fill="#d8f9e3"
      />
      <rect x="28" y="28" width="8" height="20" rx="2" fill="#f0793b" />
      <path d="M16 50h32" stroke="#d8f9e3" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}
